import * as Y from 'yjs'
import { WebsocketProvider } from 'y-websocket'

import type { TranscriptConnectionErrorType } from 'src/models/TranscriptConnectionState'
import { debug } from 'src/services/DebugService'
import { transcriptHttpClient } from 'src/network'
import { TranscriptCrdtTransport, getFatalConnectionErrorFromHttpStatus } from 'src/network/TranscriptCrdtTransport'

import { FATAL_WS_CLOSE_CODE_TO_ERROR, RECONNECT_BASE_DELAY_MS, RECONNECT_MAX_DELAY_MS, WS_4200_SESSION_ENDED } from './constants'
import { buildWebsocketDebugUrl, normalizeRoomName, normalizeSlotPath } from './utils'

export type WebSocketCrdtTransportOptions = {
    doc: Y.Doc
    jobId: string
    languageCode: string
    baseWsUrl: string
}

// `TranscriptCrdtTransport` around y-websocket's `WebsocketProvider`. Re-fetches the
// slot path per attempt (the broker may rebalance) and runs its own
// exponential-backoff reconnect on transient close codes; y-websocket's own
// retry loop pins the original URL and is torn down on every reconnect to keep
// it from racing the slot-aware attempt.
export class WebSocketCrdtTransport extends TranscriptCrdtTransport {
    private readonly _doc: Y.Doc
    private readonly _jobId: string
    private readonly _languageCode: string
    private readonly _baseWsUrl: string

    private _provider: WebsocketProvider | null = null
    private _isDisconnected = false
    private _reconnectTimerId: number | null = null
    private _reconnectAttempt = 0

    constructor(options: WebSocketCrdtTransportOptions) {
        super()

        this._doc = options.doc
        this._jobId = options.jobId
        this._languageCode = options.languageCode
        this._baseWsUrl = options.baseWsUrl
    }

    async connect(): Promise<void> {
        if (this._isDisconnected || this._provider) return

        await this._connectAttempt()
    }

    disconnect(): void {
        this._isDisconnected = true
        this._clearReconnectTimer()
        this._teardownProvider()
        super.destroy()
    }

    private _connectAttempt = async (): Promise<void> => {
        if (this._isDisconnected) return

        try {
            const { path } = await transcriptHttpClient.getSlotPath(this._jobId, this._languageCode)
            if (this._isDisconnected) return

            const slotPath = normalizeSlotPath(path)
            const roomName = normalizeRoomName(slotPath)

            debug.log('WebSocketCrdtTransport: Connecting', buildWebsocketDebugUrl(this._baseWsUrl, roomName))

            this._teardownProvider()

            const provider = new WebsocketProvider(this._baseWsUrl, roomName, this._doc, {
                WebSocketPolyfill: WebSocket,
            })
            provider.on('status', this._onStatus)
            provider.on('sync', this._onSync)
            provider.on('connection-close', this._onConnectionClose)
            this._provider = provider

            this._reconnectAttempt = 0
            this._clearReconnectTimer()
        } catch (error) {
            debug.log('WebSocketCrdtTransport: Connection attempt failed', error)

            const fatalError = getFatalConnectionErrorFromHttpStatus(error)
            if (fatalError) {
                this._emitFatal(fatalError)

                return
            }

            this._scheduleReconnect('connect_attempt_failed')
        }
    }

    private _onStatus = ({ status }: { status: 'connected' | 'disconnected' | 'connecting' }) => {
        if (this._isDisconnected) return

        debug.log('WebSocketCrdtTransport: status', status)
        this.emit('status', [status])
    }

    private _onSync = (isInitialized: boolean) => {
        if (this._isDisconnected) return

        debug.log('WebSocketCrdtTransport: sync', isInitialized)
        this.emit('sync', [isInitialized])
    }

    private _onConnectionClose = (event: CloseEvent | null) => {
        if (this._isDisconnected) return

        debug.log('WebSocketCrdtTransport: connection-close', event)

        // TODO(LG-1762): remove along with WS_4200_SESSION_ENDED when editing features go live.
        if (event?.code === WS_4200_SESSION_ENDED) {
            this._emitTerminated()

            return
        }

        const fatalError = (event?.code && FATAL_WS_CLOSE_CODE_TO_ERROR[event.code]) || null
        if (fatalError) {
            this._emitFatal(fatalError)

            return
        }

        // Tear the inner provider down so its retry loop doesn't fight ours.
        this._teardownProvider()

        if (this._reconnectTimerId !== null) return

        this._scheduleReconnect('connection_close')
    }

    private _scheduleReconnect = (reason: string) => {
        if (this._isDisconnected) return

        this._clearReconnectTimer()

        const delayMs = Math.min(RECONNECT_MAX_DELAY_MS, RECONNECT_BASE_DELAY_MS * Math.pow(2, this._reconnectAttempt))

        debug.log('WebSocketCrdtTransport: scheduling reconnect', { reason, attempt: this._reconnectAttempt + 1, delayMs })

        this._reconnectTimerId = window.setTimeout(() => {
            this._reconnectTimerId = null
            this._reconnectAttempt += 1
            void this._connectAttempt()
        }, delayMs)
    }

    private _clearReconnectTimer = () => {
        if (this._reconnectTimerId !== null) {
            window.clearTimeout(this._reconnectTimerId)
            this._reconnectTimerId = null
        }
    }

    private _teardownProvider = () => {
        if (!this._provider) return

        this._provider.off('status', this._onStatus)
        this._provider.off('sync', this._onSync)
        this._provider.off('connection-close', this._onConnectionClose)
        this._provider.disconnect()
        this._provider = null
    }

    private _emitFatal = (error: TranscriptConnectionErrorType) => {
        this._isDisconnected = true
        this._clearReconnectTimer()
        this._teardownProvider()
        this.emit('fatal', [error])
        super.destroy()
    }

    // TODO(LG-1762): remove along with WS_4200_SESSION_ENDED when editing features go live.
    private _emitTerminated = () => {
        this._isDisconnected = true
        this._clearReconnectTimer()
        this._teardownProvider()
        this.emit('terminated', [])
        super.destroy()
    }
}
